/**
 * Studio UX - Full-screen customizable canvas for various editing modes
 * Supports code, wireframes, architecture diagrams, and more
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Maximize2,
  Minimize2,
  Code,
  Layout,
  GitBranch,
  Database,
  FileText,
  Palette,
  Save,
  Undo,
  Redo,
  Download,
  Upload,
  Play,
  Settings,
  Grid,
  Type,
  Square,
  Circle,
  Triangle,
  Move,
  MousePointer,
  Pencil,
  Eraser,
  ZoomIn,
  ZoomOut,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { logger } from '../../services/loggingService';

// Lazy load heavy components
const MonacoEditor = React.lazy(() => import('@monaco-editor/react').then(m => ({ default: m.default })));

// Excalidraw - Lazy loaded in separate chunk to avoid TDZ errors
// Excalidraw and browser-fs-access use Vite's import helpers ('di', 'oA') at module level
// These helpers require Vite runtime to be fully initialized before the chunk loads
// We wait for Vite runtime ready flag and ensure main entry has executed
const Excalidraw = React.lazy(() => 
  new Promise(resolve => {
    const loadExcalidraw = async () => {
      try {
        // Wait for Vite runtime ready flag from main.tsx
        let retries = 0;
        while (!(window as any).__VITE_RUNTIME_READY__ && retries < 50) {
          await new Promise(r => setTimeout(r, 50));
          retries++;
        }
        
        // Ensure document is ready
        if (document.readyState === 'loading') {
          await new Promise(r => document.addEventListener('DOMContentLoaded', r));
        }
        
        // Additional delays to ensure Vite runtime helpers are fully initialized
        await new Promise(r => setTimeout(r, 0));
        await new Promise(r => setTimeout(r, 100));
        
        const module = await import('@excalidraw/excalidraw');
        resolve({ default: module.Excalidraw });
      } catch (error) {
        console.error('Failed to load Excalidraw:', error);
        throw error;
      }
    };
    
    // Use requestIdleCallback if available, otherwise setTimeout
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(loadExcalidraw, { timeout: 3000 });
    } else {
      setTimeout(loadExcalidraw, 200);
    }
  })
);

const ReactFlow = React.lazy(() => import('reactflow').then(m => ({ default: m.default })));

export type StudioMode = 'code' | 'wireframe' | 'architecture' | 'database' | 'document' | 'design';

interface StudioUXProps {
  projectId: string;
  blueprintId?: string;
  phaseId?: string;
  stepIndex?: number;
  initialMode?: StudioMode;
  initialContent?: any;
  onClose?: () => void;
  onSave?: (content: any) => void;
  isFullscreen?: boolean;
}

interface StudioState {
  mode: StudioMode;
  content: any;
  history: any[];
  historyIndex: number;
  zoom: number;
  gridEnabled: boolean;
  autoSave: boolean;
  theme: 'light' | 'dark';
  activeToolId?: string;
}

interface AIUpdate {
  type: 'insert' | 'replace' | 'append' | 'delete';
  position?: { line?: number; column?: number; x?: number; y?: number };
  content: any;
  animate?: boolean;
}

const STUDIO_MODES: Array<{
  id: StudioMode;
  label: string;
  icon: React.ReactNode;
  description: string;
}> = [
  { id: 'code', label: 'Code Editor', icon: <Code className="w-4 h-4" />, description: 'Write and edit code' },
  { id: 'wireframe', label: 'Wireframe', icon: <Layout className="w-4 h-4" />, description: 'Design UI wireframes' },
  { id: 'architecture', label: 'Architecture', icon: <GitBranch className="w-4 h-4" />, description: 'System architecture diagrams' },
  { id: 'database', label: 'Database', icon: <Database className="w-4 h-4" />, description: 'Database schema design' },
  { id: 'document', label: 'Document', icon: <FileText className="w-4 h-4" />, description: 'Rich text documentation' },
  { id: 'design', label: 'Design', icon: <Palette className="w-4 h-4" />, description: 'Visual design canvas' },
];

const DRAWING_TOOLS = [
  { id: 'select', icon: <MousePointer className="w-4 h-4" />, label: 'Select' },
  { id: 'pencil', icon: <Pencil className="w-4 h-4" />, label: 'Draw' },
  { id: 'rectangle', icon: <Square className="w-4 h-4" />, label: 'Rectangle' },
  { id: 'circle', icon: <Circle className="w-4 h-4" />, label: 'Circle' },
  { id: 'triangle', icon: <Triangle className="w-4 h-4" />, label: 'Triangle' },
  { id: 'text', icon: <Type className="w-4 h-4" />, label: 'Text' },
  { id: 'move', icon: <Move className="w-4 h-4" />, label: 'Move' },
  { id: 'eraser', icon: <Eraser className="w-4 h-4" />, label: 'Erase' },
];

export function StudioUX({
  projectId,
  blueprintId,
  phaseId,
  stepIndex,
  initialMode = 'code',
  initialContent = '',
  onClose,
  onSave,
  isFullscreen = false,
}: StudioUXProps) {
  const [state, setState] = useState<StudioState>({
    mode: initialMode,
    content: initialContent,
    history: [initialContent],
    historyIndex: 0,
    zoom: 100,
    gridEnabled: true,
    autoSave: true,
    theme: 'dark',
    activeToolId: 'select',
  });

  const [isFullscreenMode, setIsFullscreenMode] = useState(isFullscreen);
  const [isSaving, setIsSaving] = useState(false);
  const [aiUpdates, setAiUpdates] = useState<AIUpdate[]>([]);
  const editorRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout>();

  // Subscribe to real-time AI updates
  useEffect(() => {
    if (!projectId || !phaseId) return;

    const channel = supabase
      .channel(`studio_${projectId}_${phaseId}`)
      .on('broadcast', { event: 'ai_update' }, (payload) => {
        handleAIUpdate(payload.payload as AIUpdate);
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [projectId, phaseId]);

  // Auto-save functionality
  useEffect(() => {
    if (!state.autoSave) return;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      saveContent();
    }, 2000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [state.content]);

  const handleAIUpdate = async (update: AIUpdate) => {
    setAiUpdates(prev => [...prev, update]);

    // Apply update based on mode
    switch (state.mode) {
      case 'code':
        applyCodeUpdate(update);
        break;
      case 'wireframe':
      case 'architecture':
      case 'design':
        applyCanvasUpdate(update);
        break;
      case 'document':
        applyDocumentUpdate(update);
        break;
      case 'database':
        applyDatabaseUpdate(update);
        break;
    }

    // Animate the update if requested
    if (update.animate) {
      animateUpdate(update);
    }
  };

  const applyCodeUpdate = (update: AIUpdate) => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const model = editor.getModel();
    
    switch (update.type) {
      case 'insert':
        if (update.position?.line && update.position?.column) {
          const position = { lineNumber: update.position.line, column: update.position.column };
          editor.executeEdits('ai-update', [{
            range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
            text: update.content,
            forceMoveMarkers: true,
          }]);
        }
        break;
      
      case 'replace':
        editor.setValue(update.content);
        break;
      
      case 'append':
        const lineCount = model.getLineCount();
        const lastLineLength = model.getLineLength(lineCount);
        editor.executeEdits('ai-update', [{
          range: new monaco.Range(lineCount, lastLineLength + 1, lineCount, lastLineLength + 1),
          text: '\n' + update.content,
          forceMoveMarkers: true,
        }]);
        break;
      
      case 'delete':
        if (update.position?.line) {
          editor.executeEdits('ai-update', [{
            range: new monaco.Range(update.position.line, 1, update.position.line + 1, 1),
            text: '',
            forceMoveMarkers: true,
          }]);
        }
        break;
    }

    // Update state
    setState(prev => ({
      ...prev,
      content: editor.getValue(),
    }));
  };

  const applyCanvasUpdate = (update: AIUpdate) => {
    // For canvas-based modes, update the drawing
    if (update.type === 'insert' && update.content) {
      setState(prev => ({
        ...prev,
        content: {
          ...prev.content,
          elements: [...(prev.content.elements || []), update.content],
        },
      }));
    }
  };

  const applyDocumentUpdate = (update: AIUpdate) => {
    // For document mode, update rich text
    setState(prev => ({
      ...prev,
      content: update.type === 'replace' ? update.content : prev.content + '\n' + update.content,
    }));
  };

  const applyDatabaseUpdate = (update: AIUpdate) => {
    // For database schema updates
    if (update.type === 'insert' && update.content.table) {
      setState(prev => ({
        ...prev,
        content: {
          ...prev.content,
          tables: [...(prev.content.tables || []), update.content.table],
        },
      }));
    }
  };

  const animateUpdate = (update: AIUpdate) => {
    // Add visual feedback for AI updates
    const element = document.createElement('div');
    element.className = 'ai-update-indicator';
    element.style.cssText = `
      position: fixed;
      top: ${update.position?.y || 50}px;
      left: ${update.position?.x || 50}px;
      width: 20px;
      height: 20px;
      background: radial-gradient(circle, rgba(59, 130, 246, 0.8) 0%, transparent 70%);
      border-radius: 50%;
      pointer-events: none;
      animation: pulse 1s ease-out;
      z-index: 9999;
    `;
    document.body.appendChild(element);
    
    setTimeout(() => {
      element.remove();
    }, 1000);
  };

  const saveContent = async () => {
    setIsSaving(true);
    try {
      // Save to database
      const { error } = await supabase
        .from('studio_content')
        .upsert({
          project_id: projectId,
          blueprint_id: blueprintId,
          phase_id: phaseId,
          step_index: stepIndex,
          mode: state.mode,
          content: state.content,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      // Call parent save handler
      onSave?.(state.content);
      
      toast.success('Content saved');
    } catch (error) {
      logger.error('Error saving studio content', { error });
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const undo = () => {
    if (state.historyIndex > 0) {
      setState(prev => ({
        ...prev,
        historyIndex: prev.historyIndex - 1,
        content: prev.history[prev.historyIndex - 1],
      }));
    }
  };

  const redo = () => {
    if (state.historyIndex < state.history.length - 1) {
      setState(prev => ({
        ...prev,
        historyIndex: prev.historyIndex + 1,
        content: prev.history[prev.historyIndex + 1],
      }));
    }
  };

  const addToHistory = (content: any) => {
    setState(prev => {
      const newHistory = prev.history.slice(0, prev.historyIndex + 1);
      newHistory.push(content);
      return {
        ...prev,
        history: newHistory.slice(-50), // Keep last 50 states
        historyIndex: newHistory.length - 1,
        content,
      };
    });
  };

  const toggleFullscreen = () => {
    setIsFullscreenMode(!isFullscreenMode);
    if (!isFullscreenMode) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const changeMode = (newMode: StudioMode) => {
    // Save current content before switching
    saveContent();
    setState(prev => ({
      ...prev,
      mode: newMode,
      content: '', // Load new content for the mode
      history: [''],
      historyIndex: 0,
    }));
    loadContentForMode(newMode);
  };

  const loadContentForMode = async (mode: StudioMode) => {
    try {
      const { data } = await supabase
        .from('studio_content')
        .select('content')
        .eq('project_id', projectId)
        .eq('phase_id', phaseId)
        .eq('mode', mode)
        .single();

      if (data) {
        setState(prev => ({
          ...prev,
          content: data.content,
          history: [data.content],
          historyIndex: 0,
        }));
      }
    } catch (error) {
      logger.debug('No saved content for mode', { mode });
    }
  };

  const renderEditor = () => {
    switch (state.mode) {
      case 'code':
        return (
          <React.Suspense fallback={<div>Loading editor...</div>}>
            <MonacoEditor
              height="100%"
              language="typescript"
              theme={state.theme === 'dark' ? 'vs-dark' : 'vs'}
              value={state.content}
              onChange={(value) => addToHistory(value || '')}
              onMount={(editor) => {
                editorRef.current = editor;
              }}
              options={{
                minimap: { enabled: true },
                fontSize: 14,
                wordWrap: 'on',
                automaticLayout: true,
                formatOnPaste: true,
                formatOnType: true,
              }}
            />
          </React.Suspense>
        );

      case 'wireframe':
      case 'design':
        return (
          <React.Suspense fallback={<div>Loading canvas...</div>}>
            <div className="relative w-full h-full">
              <Excalidraw
                initialData={{
                  elements: state.content.elements || [],
                  appState: {
                    gridSize: state.gridEnabled ? 20 : null,
                    theme: state.theme,
                    zoom: { value: state.zoom / 100 },
                  },
                }}
                onChange={(elements, appState) => {
                  if (elements !== state.content.elements) {
                    addToHistory({ elements, appState });
                  }
                }}
              />
            </div>
          </React.Suspense>
        );

      case 'architecture':
        return (
          <React.Suspense fallback={<div>Loading diagram editor...</div>}>
            <div className="relative w-full h-full">
              <ReactFlow
                nodes={state.content.nodes || []}
                edges={state.content.edges || []}
                onNodesChange={(changes) => {
                  // Handle node changes
                  const newNodes = applyNodeChanges(changes, state.content.nodes || []);
                  addToHistory({ ...state.content, nodes: newNodes });
                }}
                onEdgesChange={(changes) => {
                  // Handle edge changes
                  const newEdges = applyEdgeChanges(changes, state.content.edges || []);
                  addToHistory({ ...state.content, edges: newEdges });
                }}
                fitView
              >
                <Background variant="dots" gap={20} size={1} />
                <Controls />
                <MiniMap />
              </ReactFlow>
            </div>
          </React.Suspense>
        );

      case 'database':
        return (
          <div className="w-full h-full p-4 overflow-auto">
            <DatabaseSchemaEditor
              schema={state.content}
              onChange={(schema) => addToHistory(schema)}
              theme={state.theme}
            />
          </div>
        );

      case 'document':
        return (
          <div className="w-full h-full p-8 overflow-auto">
            <RichTextEditor
              content={state.content}
              onChange={(content) => addToHistory(content)}
              theme={state.theme}
            />
          </div>
        );

      default:
        return <div>Unsupported mode</div>;
    }
  };

  const containerClass = isFullscreenMode
    ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900'
    : 'relative w-full h-full bg-white dark:bg-gray-900';

  return (
    <div className={containerClass}>
      {/* Header Toolbar */}
      <div className="flex items-center justify-between h-12 px-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        {/* Mode Selector */}
        <div className="flex items-center gap-2">
          {STUDIO_MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => changeMode(mode.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${
                state.mode === mode.id
                  ? 'bg-accent-600 text-white'
                  : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
              title={mode.description}
            >
              {mode.icon}
              <span className="hidden lg:inline">{mode.label}</span>
            </button>
          ))}
        </div>

        {/* Center Tools */}
        <div className="flex items-center gap-1">
          {(state.mode === 'wireframe' || state.mode === 'design') && (
            <>
              {DRAWING_TOOLS.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => setState(prev => ({ ...prev, activeToolId: tool.id }))}
                  className={`p-2 rounded transition-colors ${
                    state.activeToolId === tool.id
                      ? 'bg-accent-600 text-white'
                      : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                  title={tool.label}
                >
                  {tool.icon}
                </button>
              ))}
            </>
          )}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {/* Undo/Redo */}
          <button
            onClick={undo}
            disabled={state.historyIndex === 0}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Undo"
          >
            <Undo className="w-4 h-4" />
          </button>
          <button
            onClick={redo}
            disabled={state.historyIndex === state.history.length - 1}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Redo"
          >
            <Redo className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />

          {/* Zoom Controls */}
          <button
            onClick={() => setState(prev => ({ ...prev, zoom: Math.max(25, prev.zoom - 25) }))}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3rem] text-center">
            {state.zoom}%
          </span>
          <button
            onClick={() => setState(prev => ({ ...prev, zoom: Math.min(200, prev.zoom + 25) }))}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />

          {/* Grid Toggle */}
          <button
            onClick={() => setState(prev => ({ ...prev, gridEnabled: !prev.gridEnabled }))}
            className={`p-2 rounded transition-colors ${
              state.gridEnabled
                ? 'bg-accent-100 dark:bg-accent-900 text-accent-600'
                : 'hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            title="Toggle Grid"
          >
            <Grid className="w-4 h-4" />
          </button>

          {/* Auto-save Toggle */}
          <button
            onClick={() => setState(prev => ({ ...prev, autoSave: !prev.autoSave }))}
            className={`p-2 rounded transition-colors ${
              state.autoSave
                ? 'bg-green-100 dark:bg-green-900 text-green-600'
                : 'hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            title="Auto-save"
          >
            <RefreshCw className={`w-4 h-4 ${state.autoSave ? 'animate-spin' : ''}`} />
          </button>

          {/* Save Button */}
          <button
            onClick={saveContent}
            disabled={isSaving}
            className="btn btn-primary flex items-center gap-2 px-3 py-1.5 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span className="hidden lg:inline">Save</span>
          </button>

          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            title={isFullscreenMode ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreenMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* Close Button */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="relative" style={{ height: 'calc(100% - 3rem)' }}>
        {renderEditor()}

        {/* AI Update Indicators */}
        {aiUpdates.length > 0 && (
          <div className="absolute top-4 right-4 bg-accent-600 text-white px-3 py-1.5 rounded-full text-sm animate-pulse">
            AI is updating...
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(0.5);
            opacity: 1;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

// Placeholder components for specialized editors
function DatabaseSchemaEditor({ schema, onChange, theme }: any) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Database Schema Editor</h3>
      <div className="grid grid-cols-2 gap-4">
        {schema?.tables?.map((table: any, index: number) => (
          <div key={index} className="p-4 border rounded">
            <h4 className="font-medium">{table.name}</h4>
            <div className="mt-2 space-y-1 text-sm">
              {table.columns?.map((col: any, colIndex: number) => (
                <div key={colIndex} className="flex justify-between">
                  <span>{col.name}</span>
                  <span className="text-gray-500">{col.type}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RichTextEditor({ content, onChange, theme }: any) {
  return (
    <div
      contentEditable
      className="min-h-full p-4 outline-none prose dark:prose-invert max-w-none"
      onInput={(e) => onChange(e.currentTarget.innerHTML)}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

// Helper functions for React Flow
function applyNodeChanges(changes: any[], nodes: any[]) {
  // Implementation would depend on React Flow version
  return nodes;
}

function applyEdgeChanges(changes: any[], edges: any[]) {
  // Implementation would depend on React Flow version
  return edges;
}
